import { useState, useEffect, useCallback, useRef } from 'react';
import { Alert, AlertStatus, WebSocketMessage } from '../types/alerts';
import { alertApi } from '../../../../api/cansense/alertApi';
import { useWebSocket } from './useWebSocket';
import { parseAlerts } from '../utils/alertUtils';

const ALERT_FETCH_LIMIT = 100;

interface UseAlertsReturn {
  alerts: Alert[];
  suppressedAlerts: Alert[];
  alertStatus: AlertStatus | null;
  loading: boolean;
  error: string | null;
  isConnected: boolean;
  acknowledgeAlert: (id: number, suppressMinutes?: number) => Promise<void>;
  dismissAlert: (id: number, suppressMinutes?: number) => Promise<void>;
  deleteAlert: (id: number, suppressMinutes?: number) => Promise<void>;
  restoreAlert: (id: number) => Promise<void>;
  markReviewed: (id: number) => Promise<void>;
  fetchSuppressedAlerts: () => Promise<void>;
  refreshData: () => Promise<void>;
  connectionState: 'connecting' | 'connected' | 'disconnected' | 'error';
  dataSource?: string;
  warning?: string;
}

export const useAlerts = (authToken?: string): UseAlertsReturn => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [suppressedAlerts, setSuppressedAlerts] = useState<Alert[]>([]);
  const [alertStatus, setAlertStatus] = useState<AlertStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataSource, setDataSource] = useState<string | undefined>();
  const [warning, setWarning] = useState<string | undefined>();

  // Track pending operations to prevent race conditions
  const pendingOperationsRef = useRef<Set<string>>(new Set());
  const mountedRef = useRef(true);

  const refreshAlertStatus = useCallback(async () => {
    if (!mountedRef.current) return;

    try {
      const status = await alertApi.getAlertStatus();
      if (mountedRef.current) {
        setAlertStatus(status);
      }
    } catch (err) {
      console.error('Failed to refresh alert status:', err);
    }
  }, []);

  const handleWebSocketMessage = useCallback((message: WebSocketMessage) => {
    if (!mountedRef.current) return;

    console.log('Processing WebSocket message:', message);

    switch (message.type) {
      case 'alert_update':
        if (message.payload) {
          const action = message.payload.action;
          const alertData = message.payload.alert || message.payload;

          if (alertData && alertData.id) {
            const updatedAlert = alertData as Alert;

            if (action === 'deleted') {
              // Soft-delete: remove from active list, add to suppressed
              setAlerts(prev => prev.filter(alert => alert.id !== updatedAlert.id));
              setSuppressedAlerts(prev => {
                if (prev.some(a => a.id === updatedAlert.id)) return prev;
                return [...prev, { ...updatedAlert, is_suppressed: true }];
              });
              refreshAlertStatus();
            } else if (action === 'reviewed') {
              // Reviewed: auto_resolved -> resolved, remove from active list
              setAlerts(prev => prev.filter(alert => alert.id !== updatedAlert.id));
              refreshAlertStatus();
            } else if (action === 'restored') {
              // Restored: remove from suppressed, add to active
              setSuppressedAlerts(prev => prev.filter(a => a.id !== updatedAlert.id));
              setAlerts(prev => {
                if (prev.some(a => a.id === updatedAlert.id)) return prev;
                return [...prev, updatedAlert];
              });
              refreshAlertStatus();
            } else {
              // Normal upsert / acknowledged / dismissed
              setAlerts(prev => {
                const index = prev.findIndex(alert => alert.id === updatedAlert.id);
                if (index >= 0) {
                  const newAlerts = [...prev];
                  newAlerts[index] = { ...newAlerts[index], ...updatedAlert };
                  return newAlerts;
                } else if (updatedAlert.status !== 'resolved') {
                  return [...prev, updatedAlert];
                }
                return prev;
              });
              refreshAlertStatus();
            }
          }
        }
        break;

      case 'alert_delete':
        if (message.payload?.id) {
          setAlerts(prev => prev.filter(alert => alert.id !== message.payload.id));
          refreshAlertStatus();
        }
        break;

      case 'error':
        console.error('WebSocket error:', message.error);
        setError(message.error || 'WebSocket error occurred');
        break;

      case 'auth':
        // Handle authentication messages if needed
        console.log('WebSocket auth message:', message);
        break;

      default:
        console.log('Unknown WebSocket message type:', message.type);
    }
  }, [refreshAlertStatus]);

  const { isConnected, connectionState, sendMessage } = useWebSocket(
    handleWebSocketMessage,
    { authToken }
  );

  const refreshData = useCallback(async () => {
    if (!mountedRef.current) return;

    setLoading(true);
    setError(null);

    try {
      const [statusData, alertsData] = await Promise.all([
        alertApi.getAlertStatus(),
        alertApi.getAlerts(false, ALERT_FETCH_LIMIT)
      ]);

      if (mountedRef.current) {
        setAlertStatus(statusData);
        // Parse alerts to enhance with instance data
        setAlerts(parseAlerts(alertsData?.alerts || []));
        setDataSource(alertsData?.source);
        setWarning(alertsData?.warning);
      }
    } catch (err) {
      const errorMessage = err && typeof err === 'object' && 'message' in err ? (err as Error).message : 'Failed to fetch alerts';
      if (mountedRef.current) {
        setError(errorMessage);
      }
      console.error('Failed to fetch alerts:', err);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const acknowledgeAlert = useCallback(async (id: number, suppressMinutes?: number) => {
    if (!mountedRef.current) return;

    const operationId = `acknowledge-${id}`;
    if (pendingOperationsRef.current.has(operationId)) {
      console.warn(`Acknowledge operation for alert ${id} already in progress`);
      return;
    }

    try {
      pendingOperationsRef.current.add(operationId);
      setError(null);
      await alertApi.acknowledgeAlert(id, suppressMinutes);

      if (mountedRef.current) {
        setAlerts(prev =>
          prev.map(alert =>
            alert.id === id
              ? { ...alert, status: 'acknowledged' as const, updated_at: new Date().toISOString() }
              : alert
          )
        );
      }
    } catch (err) {
      const errorMessage = err && typeof err === 'object' && 'message' in err ? (err as Error).message : 'Failed to acknowledge alert';
      if (mountedRef.current) {
        setError(errorMessage);
      }
      console.error('Failed to acknowledge alert:', err);
      refreshData();
    } finally {
      pendingOperationsRef.current.delete(operationId);
    }
  }, [refreshData]);

  const dismissAlert = useCallback(async (id: number, suppressMinutes?: number) => {
    if (!mountedRef.current) return;

    const operationId = `dismiss-${id}`;
    if (pendingOperationsRef.current.has(operationId)) {
      console.warn(`Dismiss operation for alert ${id} already in progress`);
      return;
    }

    try {
      pendingOperationsRef.current.add(operationId);
      setError(null);
      await alertApi.dismissAlert(id, suppressMinutes);

      if (mountedRef.current) {
        setAlerts(prev => prev.filter(alert => alert.id !== id));
        refreshAlertStatus();
      }
    } catch (err) {
      const errorMessage = err && typeof err === 'object' && 'message' in err ? (err as Error).message : 'Failed to dismiss alert';
      if (mountedRef.current) {
        setError(errorMessage);
      }
      console.error('Failed to dismiss alert:', err);
      refreshData();
    } finally {
      pendingOperationsRef.current.delete(operationId);
    }
  }, [refreshData, refreshAlertStatus]);

  const deleteAlert = useCallback(async (id: number, suppressMinutes?: number) => {
    if (!mountedRef.current) return;

    const operationId = `delete-${id}`;
    if (pendingOperationsRef.current.has(operationId)) {
      console.warn(`Delete operation for alert ${id} already in progress`);
      return;
    }

    try {
      pendingOperationsRef.current.add(operationId);
      setError(null);
      await alertApi.deleteAlert(id, suppressMinutes);

      if (mountedRef.current) {
        setAlerts(prev => prev.filter(alert => alert.id !== id));
        refreshAlertStatus();
      }
    } catch (err) {
      const errorMessage = err && typeof err === 'object' && 'message' in err ? (err as Error).message : 'Failed to delete alert';
      if (mountedRef.current) {
        setError(errorMessage);
      }
      console.error('Failed to delete alert:', err);
      refreshData();
    } finally {
      pendingOperationsRef.current.delete(operationId);
    }
  }, [refreshData, refreshAlertStatus]);

  const restoreAlert = useCallback(async (id: number) => {
    if (!mountedRef.current) return;

    const operationId = `restore-${id}`;
    if (pendingOperationsRef.current.has(operationId)) {
      console.warn(`Restore operation for alert ${id} already in progress`);
      return;
    }

    try {
      pendingOperationsRef.current.add(operationId);
      setError(null);
      await alertApi.restoreAlert(id);

      if (mountedRef.current) {
        setSuppressedAlerts(prev => prev.filter(alert => alert.id !== id));
        refreshData();
      }
    } catch (err) {
      const errorMessage = err && typeof err === 'object' && 'message' in err ? (err as Error).message : 'Failed to restore alert';
      if (mountedRef.current) {
        setError(errorMessage);
      }
      console.error('Failed to restore alert:', err);
    } finally {
      pendingOperationsRef.current.delete(operationId);
    }
  }, [refreshData]);

  const markReviewed = useCallback(async (id: number) => {
    if (!mountedRef.current) return;

    const operationId = `review-${id}`;
    if (pendingOperationsRef.current.has(operationId)) {
      console.warn(`Review operation for alert ${id} already in progress`);
      return;
    }

    try {
      pendingOperationsRef.current.add(operationId);
      setError(null);
      await alertApi.markReviewed(id);

      if (mountedRef.current) {
        setAlerts(prev => prev.filter(alert => alert.id !== id));
        refreshAlertStatus();
      }
    } catch (err) {
      const errorMessage = err && typeof err === 'object' && 'message' in err ? (err as Error).message : 'Failed to mark alert as reviewed';
      if (mountedRef.current) {
        setError(errorMessage);
      }
      console.error('Failed to mark alert as reviewed:', err);
      refreshData();
    } finally {
      pendingOperationsRef.current.delete(operationId);
    }
  }, [refreshData, refreshAlertStatus]);

  const fetchSuppressedAlerts = useCallback(async () => {
    if (!mountedRef.current) return;

    try {
      const data = await alertApi.getAlerts(false, ALERT_FETCH_LIMIT, 0, true);
      if (mountedRef.current) {
        const suppressed = (data?.alerts || []).filter(a => a.is_suppressed);
        setSuppressedAlerts(parseAlerts(suppressed));
      }
    } catch (err) {
      console.error('Failed to fetch suppressed alerts:', err);
    }
  }, []);

  // Initial data load
  useEffect(() => {
    refreshData();
  }, [refreshData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (mountedRef.current) {
        refreshData();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [refreshData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      pendingOperationsRef.current.clear();
    };
  }, []);

  return {
    alerts,
    suppressedAlerts,
    alertStatus,
    loading,
    error,
    isConnected,
    acknowledgeAlert,
    dismissAlert,
    deleteAlert,
    restoreAlert,
    markReviewed,
    fetchSuppressedAlerts,
    refreshData,
    connectionState,
    dataSource,
    warning
  };
};